import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Trash2, Tag } from "lucide-react";

const empty = { slug: "", name_ca: "", name_es: "", name_en: "", name_fr: "", icon: "Tag", active: true, order: 100 };

export default function AdminInterests() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get("/interests")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.slug.trim() || !form.name_ca.trim()) return toast.error("Slug i nom (CA) obligatoris");
    try {
      if (editing) await api.put(`/interests/${editing.id}`, form);
      else await api.post("/interests", form);
      toast.success(editing ? "Actualitzat" : "Creat");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminar aquest interès? Els ciutadans que el tinguin el perdran.")) return;
    await api.delete(`/interests/${id}`);
    load();
  };
  const edit = (i) => { setEditing(i); setForm({ slug: i.slug, name_ca: i.name_ca, name_es: i.name_es || "", name_en: i.name_en || "", name_fr: i.name_fr || "", icon: i.icon || "Tag", active: i.active, order: i.order || 0 }); setOpen(true); };
  const toggleActive = async (i) => {
    await api.put(`/interests/${i.id}`, { ...i, active: !i.active });
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-interests">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Catàleg</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Interessos de ciutadans</h1>
          <p className="text-sm text-muted-foreground mt-2">Categories que els ciutadans podran seleccionar al registrar-se i per la segmentació dels mailings.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditing(null); } }}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="interest-new-btn"><Plus className="w-4 h-4 mr-2" /> Nou interès</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar interès" : "Nou interès"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Slug (identificador tècnic, sense espais)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "_") })} disabled={!!editing} data-testid="interest-slug" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Nom (CA) *</Label><Input value={form.name_ca} onChange={(e) => setForm({ ...form, name_ca: e.target.value })} data-testid="interest-name-ca" /></div>
                <div><Label>Nom (ES)</Label><Input value={form.name_es} onChange={(e) => setForm({ ...form, name_es: e.target.value })} /></div>
                <div><Label>Nom (EN)</Label><Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} /></div>
                <div><Label>Nom (FR)</Label><Input value={form.name_fr} onChange={(e) => setForm({ ...form, name_fr: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Icona (Lucide)</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="Tag, Utensils, Shirt…" /></div>
                <div><Label>Ordre</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></div>
              </div>
              <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="interest-active" /> <span className="text-sm">Actiu (visible al formulari públic)</span></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="interest-save-btn">Desar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(i => (
          <Card key={i.id} className={`rounded-2xl border-2 ${i.active ? "" : "opacity-60"}`} data-testid={`interest-row-${i.slug}`}>
            <CardContent className="p-5 flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Tag className="w-4 h-4" aria-hidden="true" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{i.name_ca}</div>
                <div className="text-xs text-muted-foreground font-mono">{i.slug} · ordre {i.order}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{[i.name_es, i.name_en, i.name_fr].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="flex flex-col gap-1">
                <Switch checked={i.active} onCheckedChange={() => toggleActive(i)} data-testid={`interest-toggle-${i.slug}`} />
                <Button size="icon" variant="ghost" onClick={() => edit(i)} data-testid={`interest-edit-${i.slug}`}><Edit className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(i.id)} data-testid={`interest-delete-${i.slug}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-12 text-muted-foreground">Cap interès definit. Executa el seed: <code className="text-xs">python seed_interests.py</code></div>}
      </div>
    </div>
  );
}
